import { Fieldset, TextInput, Button, Group, Text, Stack, Divider, Select, Modal, Anchor } from "@mantine/core";
import { useEffect, useState } from "react";
import { useToggle, useDisclosure } from "@mantine/hooks";

interface TesteeInfoBlock {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

export interface StartPanelProps {
    onNext: (data: TesteeInfoBlock) => void;
    initObj?: TesteeInfoBlock
}

export default function StartPanel(props: StartPanelProps) {
    const [uiLocked, toggleUiLocked] = useToggle();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [ready, setReady] = useState(false);

    const [isOpen, { open, close }] = useDisclosure(false);
    useEffect(() => {
        if (props.initObj) {
            setFirstName(props.initObj.firstName);
            setLastName(props.initObj.lastName);
            setEmail(props.initObj.email);
            setPhone(props.initObj.phone);
        }
    }, [props.initObj]);

    useEffect(() => {
        setReady(!!firstName || !!lastName || !!email || !!phone);
    }, [firstName, lastName, email, phone]);

    const handleNext = () => {
        if (uiLocked) return;
        toggleUiLocked(true);
        props.onNext({
            firstName,
            lastName,
            email,
            phone
        })
    }

    return (<Stack gap={0}>
        <Text size={'md'} mb='md'>Basic Info</Text>
        <Button variant='outline' onClick={open}>What is this?</Button>
        <Fieldset 
            legend="Personal Information"
        >
            <Group wrap="nowrap" grow>
                <TextInput label="First Name" placeholder="Steve" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                <TextInput label="Last Name" placeholder="Privacy" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </Group>
            <Group wrap="nowrap" grow>
                <TextInput label="Email" placeholder="steve@privacy.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                <TextInput label="Phone" placeholder="123-456-7890" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Group>
            <Group wrap="nowrap" grow>
                <Select label="Gender" placeholder='Male' data={['Male', 'Female', 'Other']} />
                <TextInput label="Age" placeholder='28' type='number' />
            </Group>

        </Fieldset>
        <Button mt={8} mx={'10rem'} variant="outline" onClick={handleNext} disabled={!ready}>Next</Button>
        <Modal
            opened={isOpen}
            onClose={close}
            title="About This Personality Assessment"
            centered
            size="lg"
            styles={{
                root: { position: 'fixed', top: 0, left: 0 },
                overlay: { opacity: 0.7 },
                content: {
                    padding: '20px',
                    minHeight: '300px'
                }
            }}
        >
            <Stack gap="md">
                <Text size="1.1rem">
                    The analysis typically takes just 5-7 minutes to complete.
                    This will help you and your agent understand how to best communicate and work with you.  It is the goal of this to help facilitate a smooth and easy partnership through your real-estate transaction.
                </Text>
                <Text size="1.1rem">
                    Please answer the questions based on your first instinct (gut answer) and try not to overthink them.
                </Text>

                <Text size="1.1rem" fw={500}>What is this test?</Text>
                <Text size="1.1rem">
                    The test is based on Carl Jung's theory of psychological types (with parts of the Big Five Factor added), which was later developed into the Myers-Briggs Type Indicator. It classifies people into 32 different types based on five dichotomies:
                </Text>
                <ul>
                    <li>Introversion-Extroversion</li>
                    <li>Sensing-Intuition</li>
                    <li>Thinking-Feeling</li>
                    <li>Judging-Perceiving</li>
                    <li>Assertive-Turbulent</li>
                </ul>

                <Text size="1.1rem" fw={500}>How does it work?</Text>
                <Text size="1.1rem">
                    The test consists of several sections:
                </Text>
                <Text size="1.1rem" c="dimmed">
                    Please answer the questions based on your first instinct (gut answer) and try not to overthink them. 
                </Text>
                <ol>
                    <li>First section: Choose between opposing personality descriptions on a five-point scale</li>
                    <li>Second section: Rate how much you agree with first-person statements</li>
                </ol>

                <Text size="1.1rem">
                    The test typically takes 6-8 minutes to complete. Then, you will be presented with information on your personality type based on the answers.
                </Text>

                <Text size="1rem" c="dimmed">
                    Note: This is not psychological advice. Your responses will be anonymously saved and may be used for research purposes.
                </Text>

                <Divider my="sm" />

                <Text size="1rem" fw={500}>References:</Text>
                <Text size="1rem">
                    • Eric Jorgenson (2014). <Anchor href="https://openpsychometrics.org/tests/OEJTS/development/" target="_blank">"Development of the Open Extended Jungian Type Scales"</Anchor>
                </Text>
                <Text size="1rem">
                    • James H. Reynierse (2012). <Anchor href="https://www.capt.org/research/article/JPT_Vol72_01_0312.pdf" target="_blank">"Toward an Empirically Sound and Radically Revised Type Theory"</Anchor>. The Journal of Psychological Type 72(1)
                </Text>
            </Stack>
        </Modal>
    </Stack>)
}